/**
 * MemberProfileModal — modal version of MemberProfile.
 * Opens when clicking a member avatar in GroupPage.
 */

import { useState } from 'react';
import { trpc } from '../utils/trpc';
import styles from '../styles/MemberProfile.module.css';

interface MemberProfileModalProps {
  username: string;
  onClose: () => void;
}

export default function MemberProfileModal({
  username,
  onClose,
}: MemberProfileModalProps) {
  const [showMenu, setShowMenu] = useState(false);
  const [reported, setReported] = useState(false);
  const [showTags, setShowTags] = useState(false);

  const {
    data: profile,
    isLoading: profileLoading,
    error,
  } = trpc.member.getByUsername.useQuery({ username }, { enabled: !!username });

  const { data: userTags, isLoading: tagsLoading } = trpc.tags.getUserTags.useQuery(
    { userId: profile?.id || '' },
    { enabled: !!profile?.id },
  );

  const handleReport = () => {
    setReported(true);
    setShowMenu(false);
  };

  const handleBackdrop = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) onClose();
  };

  const isLoading = profileLoading || tagsLoading;

  if (isLoading) {
    return (
      <div className={styles.backdrop} onClick={handleBackdrop}>
        <div className={styles.profileCard}>
          <div className={styles.heroBanner} />
          <button className={styles.closeButton} onClick={onClose} aria-label="Close">
            ✕
          </button>
          <div className={styles.cardInner}>
            <div className={styles.skeletonAvatar} />
            <div className={styles.skeletonTitle} />
            <div className={styles.skeletonText} />
            <div className={styles.skeletonText} />
            <div className={styles.skeletonTags}>
              <div className={styles.skeletonTag} />
              <div className={styles.skeletonTag} />
              <div className={styles.skeletonTag} />
              <div className={styles.skeletonTag} />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !profile) {
    return (
      <div className={styles.backdrop} onClick={handleBackdrop}>
        <div className={styles.profileCard}>
          <div className={styles.heroBanner} />
          <button className={styles.closeButton} onClick={onClose} aria-label="Close">
            ✕
          </button>
          <div className={styles.cardInner}>
            <div className={styles.errorContent}>
              <div className={styles.errorIcon}>🙁</div>
              <h2 className={styles.errorTitle}>Profile Not Found</h2>
              <p className={styles.errorMessage}>
                {error ? error.message : `The user @${username} doesn't exist.`}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const tags = userTags?.map((ut: any) => ut.tags.name) || [];
  const fullName = [profile.first_name, profile.last_name].filter(Boolean).join(' ');

  return (
    <div className={styles.backdrop} onClick={handleBackdrop}>
      <div className={styles.profileCard}>
        {/* Hero Banner */}
        <div className={styles.heroBanner} />

        {/* Controls */}
        <div className={styles.menuContainer}>
          <button
            className={styles.menuButton}
            onClick={() => setShowMenu(!showMenu)}
            aria-label="Options menu"
          >
            ⋮
          </button>
          {showMenu && (
            <div className={styles.dropdown}>
              <button
                className={styles.dropdownItem}
                onClick={handleReport}
                disabled={reported}
              >
                {reported ? '✓ Reported' : '🚩 Report Member'}
              </button>
            </div>
          )}
        </div>

        <button className={styles.closeButton} onClick={onClose} aria-label="Close">
          ✕
        </button>

        {/* Card Body */}
        <div className={styles.cardInner}>
          {profile.profile_picture_url ? (
            <img
              onLoad={(e) => e.currentTarget.classList.add('loaded')}
              src={profile.profile_picture_url}
              alt={`${profile.username}'s profile`}
              className={styles.profilePicture}
            />
          ) : (
            <div className={styles.defaultAvatar}>
              {profile.username.charAt(0).toUpperCase()}
            </div>
          )}

          <h1 className={styles.username}>@{profile.username}</h1>

          {(fullName || profile.pronouns) && (
            <div className={styles.subLine}>
              {fullName && <span className={styles.subName}>{fullName}</span>}
              {fullName && profile.pronouns && <span className={styles.subDot}>•</span>}
              {profile.pronouns && (
                <span className={styles.subPronouns}>{profile.pronouns}</span>
              )}
            </div>
          )}

          {profile.description && (
            <div className={styles.description}>{profile.description}</div>
          )}

          <div className={styles.tagsSection}>
            <div className={styles.tagsHeader}>
              <h2 className={styles.tagsTitle}>Interests</h2>
              <button
                className={styles.tagsToggle}
                onClick={() => setShowTags(!showTags)}
                aria-expanded={showTags}
              >
                {showTags ? 'Hide' : 'View'}
                <span
                  className={`${styles.tagsChevron} ${
                    showTags ? styles.tagsChevronOpen : ''
                  }`}
                >
                  ▼
                </span>
              </button>
            </div>
            <div className={`${styles.tagsBody} ${showTags ? styles.tagsBodyOpen : ''}`}>
              {profile.is_private ? (
                <div className={styles.emptyTags}>
                  <p className={styles.emptyTagsText}>This profile is private</p>
                </div>
              ) : tags.length > 0 ? (
                <div className={styles.tagsContainer}>
                  {tags.map((tag: string, index: number) => (
                    <span key={index} className={styles.tag}>
                      {tag}
                    </span>
                  ))}
                </div>
              ) : (
                <div className={styles.emptyTags}>
                  <p className={styles.emptyTagsText}>No interests yet</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
